import React from "react";
import { useApplication } from "../ApplicationProvider";
import { usePublicConfig } from "../PublicConfigProvider";
import { TemplateUpload } from "../TemplateUpload";
import { SignaturePad } from "../SignaturePad";
import { applicantCertificationText, parentCertificationText } from "../../attestationText";

interface CertDoc {
  title: string;
  body: string;
}

// ---------------------------------------------------------------------------
// Final approved on-screen legal text (Round 9). VERBATIM — do not edit, reword,
// summarize, or reformat the wording below. Each item has a Boys and a Girls
// version; the correct one is selected per app from cfg.program.
// ---------------------------------------------------------------------------

const BOYS_CODE_OF_CONDUCT = `If accepted to Maryland American Legion Boys State, I understand that I am expected to conduct myself at all times in a respectful, responsible, and appropriate manner. I agree to follow all rules, regulations, policies, schedules, and expectations of the program, including those explained during online or in-person orientations and those provided by The American Legion Department of Maryland, my sponsoring Post or Unit, or Boys State staff.

I understand that Maryland Boys State is a structured leadership and citizenship program, and I agree to obey the lawful and reasonable directions of all authorized staff members, counselors, instructors, and program officials. I further understand that failure to follow program rules, staff instructions, standards of conduct, safety requirements, or expectations of personal responsibility may result in disciplinary action, including possible dismissal from the program.

I agree that I will not leave the Maryland Boys State program site or any assigned program activity except in an emergency or with authorization from program staff and my parent or legal guardian.

I certify that I have read, understand, and agree to comply with the information, rules, regulations, and expectations provided to me by Maryland Boys State, The American Legion Department Headquarters, and my sponsoring Post or Unit.

I also certify that I have not previously attended, and do not plan to attend, another Boys State program.`;

const GIRLS_CODE_OF_CONDUCT = `If accepted to Maryland American Legion Auxiliary Girls State, I understand that I am expected to conduct myself at all times in a respectful, responsible, and appropriate manner. I agree to follow all rules, regulations, policies, schedules, and expectations of the program, including those explained during online or in-person orientations and those provided by the American Legion Auxiliary Department of Maryland, my sponsoring Unit, or Girls State staff.

I understand that Maryland Girls State is a structured leadership and citizenship program, and I agree to obey the lawful and reasonable directions of all authorized staff members, counselors, instructors, and program officials. I further understand that failure to follow program rules, staff instructions, standards of conduct, safety requirements, or expectations of personal responsibility may result in disciplinary action, including possible dismissal from the program.

I agree that I will not leave the Maryland Girls State program site or any assigned program activity except in an emergency or with authorization from program staff and my parent or legal guardian.

I certify that I have read, understand, and agree to comply with the information, rules, regulations, and expectations provided to me by Maryland Girls State, the American Legion Auxiliary Department of Maryland, and my sponsoring Unit.

I also certify that I have not previously attended, and do not plan to attend, another Girls State program.`;

const BOYS_PHOTO_RELEASE = `As the parent or legal guardian of the applicant, I understand that during my child's participation in Maryland Boys State, photographs, video recordings, audio recordings, interviews, livestreams, written statements, social media content, and other media may be created by or on behalf of the program, The American Legion, the host institution, invited guests, media representatives, or other authorized persons.

I grant permission to Maryland Boys State, The American Legion, its affiliated organizations, sponsors, partners, representatives, and designees to photograph, record, interview, livestream, publish, display, reproduce, distribute, and otherwise use my child's name, image, likeness, voice, statements, and participation in program activities for educational, historical, promotional, recruitment, social media, website, news, archival, and public relations purposes.

I understand that these materials may be used in print, digital, video, broadcast, social media, websites, presentations, news releases, program publications, and other media formats without further notice, approval, or compensation. I further understand that once published or shared, such materials may be viewed, copied, or redistributed by others beyond the control of the program or The American Legion.

I waive any right to inspect or approve the final materials or the use to which they may be applied. I understand that no compensation will be provided for the use of my child's name, image, likeness, voice, statements, or participation.

To the fullest extent permitted by law, I release and hold harmless Maryland Boys State, The American Legion, its affiliated organizations, sponsors, partners, officers, employees, volunteers, representatives, and designees from any claims, demands, or liability arising from the use of such photographs, recordings, interviews, statements, or other media as described above.

By signing or submitting this application, I certify that I am the parent or legal guardian of the applicant and that I have read, understand, and agree to this Photo, Video, Media, and Publicity Release on behalf of my child.`;

const GIRLS_PHOTO_RELEASE = `As the parent or legal guardian of the applicant, I understand that during my child's participation in Maryland Girls State, photographs, video recordings, audio recordings, interviews, livestreams, written statements, social media content, and other media may be created by or on behalf of the program, The American Legion Auxiliary, the host institution, invited guests, media representatives, or other authorized persons.

I grant permission to Maryland Girls State, The American Legion Auxiliary, its affiliated organizations, sponsors, partners, representatives, and designees to photograph, record, interview, livestream, publish, display, reproduce, distribute, and otherwise use my child's name, image, likeness, voice, statements, and participation in program activities for educational, historical, promotional, recruitment, social media, website, news, archival, and public relations purposes.

I understand that these materials may be used in print, digital, video, broadcast, social media, websites, presentations, news releases, program publications, and other media formats without further notice, approval, or compensation. I further understand that once published or shared, such materials may be viewed, copied, or redistributed by others beyond the control of the program or The American Legion Auxiliary.

I waive any right to inspect or approve the final materials or the use to which they may be applied. I understand that no compensation will be provided for the use of my child's name, image, likeness, voice, statements, or participation.

To the fullest extent permitted by law, I release and hold harmless Maryland Girls State, The American Legion Auxiliary, its affiliated organizations, sponsors, partners, officers, employees, volunteers, representatives, and designees from any claims, demands, or liability arising from the use of such photographs, recordings, interviews, statements, or other media as described above.

By signing or submitting this application, I certify that I am the parent or legal guardian of the applicant and that I have read, understand, and agree to this Photo, Video, Media, and Publicity Release on behalf of my child.`;

const BOYS_HOLD_HARMLESS = `That I (we), parent(s) or legal guardian(s) of this minor child, in consideration of The American Legion, Department of Maryland American Legion Boys State Program, I, we, or theirs, executors, administrators and assigns, hereby release and discharge(s) the said American Legion, Department of Maryland, Inc., its agents, servants and employees, or cosponsors, and third parties including McDaniel College and its agents, servants, and employees, or co-sponsors from any and all claims, whatever kind or nature for or because of any matter or thing done, omitted or suffered to be done by the said American Legion, Department of Maryland, Inc., its agents, servants and employees, third parties or co-sponsors, particularly for or on account of any injuries or illnesses to person or injury to property, of any child or ward during the period the child or ward is a member of The Maryland American Legion Boys State and participating in the program. Furthermore, I (we) hereby grant permission and consent for my, (our) child or ward to be treated at any medical facility or uniformed services hospital to which the child or ward is taken by Maryland American Legion Boys State Officials due to illness or injury. I hereby assign and grant to Maryland Boys State and The American Legion the right and permission to use and publish the photographs/film/videotapes/electronic representations and/or sound recordings made of me or my child at all American Legion activities, and I hereby release The American Legion, Departments, the activity coordinators, and all employees, volunteers, related parties, or other organizations associated with the activity from any and all liability from such use and publication. I hereby authorize the reproduction, sale, copyright, exhibit, broadcast, electronic storage, and/or distribution of said photographs/film/videotapes/electronic representations and/or sound recordings without limitation at the discretion of The American Legion, and I specifically waive any right to any compensation I may have for any of the foregoing. Further, I hereby grant permission for my son or ward, to participate in trips and activities off the Campus, with the same considerations as above. Such as bus trip to Camp Fretterd in Reisterstown, Maryland or the State House in Annapolis, Maryland.`;

const GIRLS_HOLD_HARMLESS = `Know all by these presents:

That I (we), parent(s) or legal guardian(s) of this minor child, in consideration of The American Legion Auxiliary, Department of Maryland American Legion Auxiliary Girls State Program, I, we, or theirs, executors, administrators and assigns, hereby release and discharge(s) the said American Legion Auxiliary, Department of Maryland, Inc., its agents, servants and employees, or cosponsors, and third parties including Salisbury University and its agents, servants, and employees, or co-sponsors from any and all claims, whatever kind or nature for or because of any matter or thing done, omitted or suffered to be done by the said American Legion Auxiliary, Department of Maryland, Inc., its agents, servants and employees, third parties or co-sponsors, particularly for or on account of any injuries or illnesses to person or injury to property, of any child or ward during the period the child or ward is an attendee of The Maryland American Legion Auxiliary Girls State and participating in the program. Furthermore, I (we) hereby grant permission and consent for my, (our) child or ward to be treated at any medical facility or uniformed services hospital to which the child or ward is taken by Maryland American Legion Auxiliary Girls State Officials due to illness or injury. I hereby assign and grant to Maryland Girls State and The American Legion Auxiliary, Department of Maryland the right and permission to use and publish the photographs/film/videotapes/electronic representations and/or sound recordings made of me or my child at all American Legion activities, and I hereby release The American Legion, Departments, the activity coordinators, and all employees, volunteers, related parties, or other organizations associated with the activity from any and all liability from such use and publication. I hereby authorize the reproduction, sale, copyright, exhibit, broadcast, electronic storage, and/or distribution of said photographs/film/videotapes/electronic representations and/or sound recordings without limitation at the discretion of The American Legion Auxiliary, Department of Maryland, Inc., and I specifically waive any right to any compensation I may have for any of the foregoing. Further, I hereby grant permission for my child or ward, to participate in Girls State trips off the Campus, with the same considerations as above.`;

const BOYS_MEDICAL_TREATMENT = `As the parent or legal guardian of the applicant, I authorize the staff, volunteers, representatives, and designated medical personnel of Maryland Boys State and The American Legion, Department of Maryland, acting through the Maryland Boys State Director or the Director's designee, to obtain, arrange, or consent to medical evaluation, care, and treatment for my child if such care is deemed necessary or appropriate during my child's participation in the program.

This authorization includes, but is not limited to, permission to obtain or provide first aid, emergency medical services, physician care, urgent care, hospital care, diagnostic testing, X-rays, laboratory tests, routine medical treatment, prescribed medications, necessary transportation, and any other medical services reasonably required for the health, safety, and welfare of my child.

In the event of an emergency, I understand that reasonable efforts will be made to contact me or another listed emergency contact as soon as practicable. However, if I cannot be reached in a timely manner, I authorize the physician, medical provider, emergency medical personnel, hospital, or other health care provider selected by or contacted on behalf of the Maryland Boys State Director or the Director's designee to secure, administer, or arrange necessary medical treatment, including hospitalization, for my child.

I understand that this authorization is intended to allow necessary medical decisions to be made without delay when my child's health or safety may require prompt attention. I further understand that I remain responsible for any medical, transportation, hospital, emergency, or related expenses incurred on behalf of my child that are not otherwise covered by insurance or other applicable coverage.

I authorize copies, scans, or photocopies of this form to be used with the same force and effect as the original, including for trips, transportation, activities, or events occurring away from the program campus.

By signing or submitting this application, I certify that I am the parent or legal guardian of the applicant and that I have read, understand, and agree to this Authorization for Medical Treatment on behalf of my child.`;

const GIRLS_MEDICAL_TREATMENT = `As the parent or legal guardian of the applicant, I authorize the staff, volunteers, representatives, and designated medical personnel of Maryland Girls State and The American Legion Auxiliary, Department of Maryland, acting through the Maryland Girls State Director or the Director's designee, to obtain, arrange, or consent to medical evaluation, care, and treatment for my child if such care is deemed necessary or appropriate during my child's participation in the program.

This authorization includes, but is not limited to, permission to obtain or provide first aid, emergency medical services, physician care, urgent care, hospital care, diagnostic testing, X-rays, laboratory tests, routine medical treatment, prescribed medications, necessary transportation, and any other medical services reasonably required for the health, safety, and welfare of my child.

In the event of an emergency, I understand that reasonable efforts will be made to contact me or another listed emergency contact as soon as practicable. However, if I cannot be reached in a timely manner, I authorize the physician, medical provider, emergency medical personnel, hospital, or other health care provider selected by or contacted on behalf of the Maryland Girls State Director or the Director's designee to secure, administer, or arrange necessary medical treatment, including hospitalization, for my child.

I understand that this authorization is intended to allow necessary medical decisions to be made without delay when my child's health or safety may require prompt attention. I further understand that I remain responsible for any medical, transportation, hospital, emergency, or related expenses incurred on behalf of my child that are not otherwise covered by insurance or other applicable coverage.

I authorize copies, scans, or photocopies of this form to be used with the same force and effect as the original, including for trips, transportation, activities, or events occurring away from the program campus.

By signing or submitting this application, I certify that I am the parent or legal guardian of the applicant and that I have read, understand, and agree to this Authorization for Medical Treatment on behalf of my child.`;

function certTexts(program: "boys" | "girls"): {
  codeOfConduct: CertDoc;
  financialResponsibility: CertDoc;
  holdHarmless: CertDoc;
  photoRelease: CertDoc;
  medicalTreatment: CertDoc;
} {
  const boys = program === "boys";
  const programLabel = boys ? "Boys State" : "Girls State";
  return {
    codeOfConduct: {
      title: boys
        ? "American Legion Maryland Boys State Code of Conduct Acknowledgment"
        : "American Legion Auxiliary Maryland Girls State Code of Conduct Acknowledgment",
      body: boys ? BOYS_CODE_OF_CONDUCT : GIRLS_CODE_OF_CONDUCT,
    },
    // Brief neutral instruction only — the parent reads, completes, initials, and
    // signs the downloadable paper form itself, so the full legal text is NOT
    // inlined here (see the downloadable Agreement of Financial Responsibility).
    financialResponsibility: {
      title: "Agreement of Financial Responsibility",
      body: `Download the Agreement of Financial Responsibility below, have a parent or legal guardian read, complete, initial, and sign it, then upload the completed form. The parent or guardian signs the paper form itself — there is no on-screen signature for this item.`,
    },
    holdHarmless: {
      // Retitled: the document itself dropped "Hold Harmless" and
      // "Indemnity Agreement" after a legal reviewer flagged that they
      // implied an indemnification obligation it does not create. LABEL
      // ONLY — `body` below is the original text still under JA review.
      title: boys
        ? "2027 Maryland Boys State — Release and Waiver of Liability Agreement"
        : "2027 Maryland Girls State — Release and Waiver of Liability Agreement",
      body: boys ? BOYS_HOLD_HARMLESS : GIRLS_HOLD_HARMLESS,
    },
    photoRelease: {
      title: "Photo, Video, Media, and Publicity Release for Minor Participant",
      body: boys ? BOYS_PHOTO_RELEASE : GIRLS_PHOTO_RELEASE,
    },
    medicalTreatment: {
      title: `Authorization for Medical Treatment — Maryland ${programLabel}`,
      body: boys ? BOYS_MEDICAL_TREATMENT : GIRLS_MEDICAL_TREATMENT,
    },
  };
}

// Long legal text shown in a readable, scrollable block.
function LegalText({ children }: { children: string }) {
  return (
    <div className="my-3 max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-line">
      {children}
    </div>
  );
}

export function Step5Certifications({ errors }: { errors: Record<string, string> }) {
  const { data, update } = useApplication();
  const cfg = usePublicConfig();
  const text = certTexts(cfg.program);

  const setS = <K extends keyof typeof data.step5>(key: K, value: (typeof data.step5)[K]) =>
    update((d) => {
      d.step5[key] = value as never;
    });

  return (
    <section className="space-y-6">
      <div className="section-card">
        <h2 className="text-xl font-semibold mb-2">Certifications & Acknowledgements</h2>
        <p className="text-sm text-slate-600 mb-4">
          Please read each certification below. Some are acknowledged and signed on screen; others
          must be downloaded, signed by a parent/guardian, and uploaded.
        </p>
      </div>

      {/* Sub-section 1 — Applicant */}
      <div>
        <h3 className="text-xl font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">
          Applicant
        </h3>
        <div className="section-card">
          <h3 className="text-lg font-semibold">{text.codeOfConduct.title}</h3>
          <LegalText>{text.codeOfConduct.body}</LegalText>
          <label className="flex items-start gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={data.step5.codeOfConductAck}
              onChange={(e) => setS("codeOfConductAck", e.target.checked)}
              className="mt-1 h-4 w-4 border-slate-300 rounded text-program focus:ring-program"
            />
            <span className="text-sm">I acknowledge that I have read and agree to the Code of Conduct.</span>
          </label>
          {errors["codeOfConductAck"] && <p className="field-error">{errors["codeOfConductAck"]}</p>}
          <div className="mt-4 max-w-md">
            <SignaturePad
              label="Code of conduct signature"
              value={data.step5.codeOfConductSignature}
              onChange={(v) => setS("codeOfConductSignature", v)}
              error={errors["codeOfConductSignature"]}
            />
          </div>
        </div>

        {/* Round 17 — Applicant Certification (required to submit) */}
        <div className="section-card">
          <h3 className="text-lg font-semibold">Applicant Certification</h3>
          <LegalText>{applicantCertificationText()}</LegalText>
          <label className="flex items-start gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={data.step5.applicantCertificationAck}
              onChange={(e) => setS("applicantCertificationAck", e.target.checked)}
              className="mt-1 h-4 w-4 border-slate-300 rounded text-program focus:ring-program"
            />
            <span className="text-sm">
              I have read and agree to the Applicant Certification above.
            </span>
          </label>
          {errors["applicantCertificationAck"] && (
            <p className="field-error">{errors["applicantCertificationAck"]}</p>
          )}
        </div>
      </div>

      {/* Sub-section 2 — Parent(s) */}
      <div>
        <h3 className="text-xl font-semibold text-slate-900 border-b border-slate-200 pb-2">
          Parent(s)
        </h3>
        <p className="text-sm text-slate-600 mt-2 mb-4">
          This section must be signed by at least one parent or guardian.
        </p>

        <div className="section-card">
          <h3 className="text-lg font-semibold">{text.financialResponsibility.title}</h3>
          <p className="text-sm text-slate-600 my-3">{text.financialResponsibility.body}</p>
          <label className="flex items-start gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={data.step5.financialResponsibilityAck}
              onChange={(e) => setS("financialResponsibilityAck", e.target.checked)}
              className="mt-1 h-4 w-4 border-slate-300 rounded text-program focus:ring-program"
            />
            <span className="text-sm">
              I acknowledge that I have read and agree to the Agreement of Financial Responsibility.
            </span>
          </label>
          {errors["financialResponsibilityAck"] && <p className="field-error">{errors["financialResponsibilityAck"]}</p>}
          <div className="mt-4">
            <TemplateUpload
              templateName="financial-responsibility"
              fieldKey="financial-responsibility"
              helper="Download, have a parent/guardian sign, then upload the completed form."
            />
          </div>
        </div>

        <div className="section-card">
          <h3 className="text-lg font-semibold">{text.holdHarmless.title}</h3>
          <LegalText>{text.holdHarmless.body}</LegalText>
          <label className="flex items-start gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={data.step5.liabilityWaiverAck}
              onChange={(e) => setS("liabilityWaiverAck", e.target.checked)}
              className="mt-1 h-4 w-4 border-slate-300 rounded text-program focus:ring-program"
            />
            <span className="text-sm">
              I acknowledge that I have read and agree to the Release and Waiver of Liability Agreement.
            </span>
          </label>
          {errors["liabilityWaiverAck"] && <p className="field-error">{errors["liabilityWaiverAck"]}</p>}
          <div className="mt-4">
            <TemplateUpload
              templateName="liability-waiver"
              fieldKey="liability-waiver"
              helper="Download, have a parent/guardian sign, then upload the completed form."
            />
          </div>
        </div>

        <div className="section-card">
          <h3 className="text-lg font-semibold">{text.photoRelease.title}</h3>
          <LegalText>{text.photoRelease.body}</LegalText>
          <label className="flex items-start gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={data.step5.photoReleaseAck}
              onChange={(e) => setS("photoReleaseAck", e.target.checked)}
              className="mt-1 h-4 w-4 border-slate-300 rounded text-program focus:ring-program"
            />
            <span className="text-sm">I acknowledge that I have read and agree to the Photo, Video, Media, and Publicity Release.</span>
          </label>
          {errors["photoReleaseAck"] && <p className="field-error">{errors["photoReleaseAck"]}</p>}
          <div className="mt-4 max-w-md">
            <SignaturePad
              label="Photo / Media release signature"
              value={data.step5.photoReleaseSignature}
              onChange={(v) => setS("photoReleaseSignature", v)}
              error={errors["photoReleaseSignature"]}
            />
          </div>
        </div>

        <div className="section-card">
          <h3 className="text-lg font-semibold">{text.medicalTreatment.title}</h3>
          <LegalText>{text.medicalTreatment.body}</LegalText>
          <label className="flex items-start gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={data.step5.medicalTreatmentAck}
              onChange={(e) => setS("medicalTreatmentAck", e.target.checked)}
              className="mt-1 h-4 w-4 border-slate-300 rounded text-program focus:ring-program"
            />
            <span className="text-sm">
              I acknowledge that I have read and agree to the Authorization for Medical Treatment.
            </span>
          </label>
          {errors["medicalTreatmentAck"] && <p className="field-error">{errors["medicalTreatmentAck"]}</p>}
          <div className="mt-4 max-w-md">
            <SignaturePad
              label="Parent / Guardian signature (Authorization for Medical Treatment)"
              value={data.step5.medicalTreatmentSignature}
              onChange={(v) => setS("medicalTreatmentSignature", v)}
              error={errors["medicalTreatmentSignature"]}
            />
          </div>
        </div>

        {/* Round 17 — Parent/Guardian Certification (required to submit) */}
        <div className="section-card">
          <h3 className="text-lg font-semibold">Parent/Guardian Certification</h3>
          <LegalText>{parentCertificationText(cfg.programName)}</LegalText>
          <label className="flex items-start gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={data.step5.parentCertificationAck}
              onChange={(e) => setS("parentCertificationAck", e.target.checked)}
              className="mt-1 h-4 w-4 border-slate-300 rounded text-program focus:ring-program"
            />
            <span className="text-sm">
              I have read and agree to the Parent/Guardian Certification above.
            </span>
          </label>
          {errors["parentCertificationAck"] && (
            <p className="field-error">{errors["parentCertificationAck"]}</p>
          )}
        </div>
      </div>

      <div className="section-card">
        <h3 className="text-lg font-semibold mb-2">Digital signatures</h3>
        <p className="text-sm text-slate-600 mb-4">
          In addition to the signed forms uploaded above, please sign below to confirm submission.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <SignaturePad
              label="Parent / Guardian signature"
              value={data.step5.parentGuardianSignature}
              onChange={(v) => setS("parentGuardianSignature", v)}
              error={errors["parentGuardianSignature"]}
            />
            <label className="field-label mt-3">Date</label>
            <input
              type="date"
              className="field-input"
              value={data.step5.parentGuardianSignatureDate}
              onChange={(e) => setS("parentGuardianSignatureDate", e.target.value)}
            />
            {errors["parentGuardianSignatureDate"] && (
              <p className="field-error">{errors["parentGuardianSignatureDate"]}</p>
            )}
          </div>
          <div>
            <SignaturePad
              label="Applicant signature"
              value={data.step5.applicantSignature}
              onChange={(v) => setS("applicantSignature", v)}
              error={errors["applicantSignature"]}
            />
            <label className="field-label mt-3">Date</label>
            <input
              type="date"
              className="field-input"
              value={data.step5.applicantSignatureDate}
              onChange={(e) => setS("applicantSignatureDate", e.target.value)}
            />
            {errors["applicantSignatureDate"] && (
              <p className="field-error">{errors["applicantSignatureDate"]}</p>
            )}
          </div>
        </div>
      </div>

      <div className="section-card">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data.step5.finalAttestation}
            onChange={(e) => setS("finalAttestation", e.target.checked)}
            className="mt-1 h-4 w-4 border-slate-300 rounded text-program focus:ring-program"
          />
          <span className="text-sm">
            <strong>Final attestation:</strong> All information provided is true and accurate to the best of my knowledge.
          </span>
        </label>
        {errors["finalAttestation"] && <p className="field-error">{errors["finalAttestation"]}</p>}
      </div>
    </section>
  );
}
